import { jsx as _jsx } from "react/jsx-runtime";
import { Controls, ReactFlow, ReactFlowProvider, applyNodeChanges } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import { AddAssetNode } from "../CustomNodes/AddAssetNode";
import { NodeIconType } from "../CustomNodes/CustomNodeEnums";
import { UtilityPowerNode } from "../CustomNodes/UtilityPowerNode";
import { UtilityProviderNode } from "../CustomNodes/UtilityProviderNode";
import { AssetType } from "./AddAssetsListCard";
const DEFAULT_VIEWPORT_ZOOM = 0.5;
const DEFAULT_VIEWPORT = { x: 0, y: 0, zoom: DEFAULT_VIEWPORT_ZOOM };
const NODE_FIRST_Y = 100;
const NODE_Y_STEP = 150;
const ESS_X = 750;
const MACHINE_X = 900;
const UTILITY_POWER_NODE_X = 300;
const UTILITY_POWER_NODE_Y = 340;
const CHARGE_STATION_NODE_X = 450;
const CHARGE_STATION_NODE_Y = 250;
const ESS_LABEL_LENGTH = 5;
const MACHINE_LABEL_LENGTH = 3;
const EDGE_STROKE_WIDTH = 3;
const AddAssetNetworkDiagramUI = ({ assetList }) => {
    const [nodes, setNodes] = React.useState([]);
    const onNodesChange = useCallback((changes) => {
        setNodes((nds) => applyNodeChanges(changes, nds));
    }, [setNodes]);
    const getAssetIconType = (type) => {
        switch (type) {
            case AssetType.ESS:
                return NodeIconType.ESSBOX;
            case AssetType.MACHINE:
                return NodeIconType.EXCAVATOR;
            default:
                return NodeIconType.EXCAVATOR;
        }
    };
    const getNetworkNodes = (assetList) => {
        const nodeList = [];
        const utilityPowerNodePosition = { x: UTILITY_POWER_NODE_X, y: UTILITY_POWER_NODE_Y };
        const chargeStationNodePosition = { x: CHARGE_STATION_NODE_X, y: CHARGE_STATION_NODE_Y };
        let ESS_Y_VALUE = NODE_FIRST_Y;
        let MACHINE_Y_VALUE = NODE_FIRST_Y;
        nodeList.push({
            data: {},
            id: "Power",
            position: utilityPowerNodePosition,
            type: "utilityPowerNode"
        });
        nodeList.push({
            data: { label: " Utility Provider" },
            id: "utilityProviderNode",
            position: chargeStationNodePosition,
            type: "utilityProviderNode"
        });
        assetList.forEach((asset) => {
            const { assetSerialNumber, assetSourceId, assetSourceType } = asset;
            const type = assetSourceType;
            // If assetId does not exist, this is a new asset that has not been onboarded yet.
            const assetId = asset.assetId ? asset.assetId : `${assetSourceId}-${assetSerialNumber}`;
            if (type === AssetType.ESS) {
                nodeList.push({
                    data: {
                        columnTitle: ESS_Y_VALUE === NODE_FIRST_Y ? "ESS #" : "",
                        iconType: getAssetIconType(type),
                        label: assetSerialNumber.substring(0, ESS_LABEL_LENGTH)
                    },
                    id: assetId,
                    position: { x: ESS_X, y: ESS_Y_VALUE },
                    type: "addAssetNode"
                });
                ESS_Y_VALUE = ESS_Y_VALUE + NODE_Y_STEP;
            }
            if (type === AssetType.MACHINE) {
                nodeList.push({
                    data: {
                        columnTitle: MACHINE_Y_VALUE === NODE_FIRST_Y ? "Asset #" : "",
                        iconType: getAssetIconType(type),
                        label: assetSerialNumber.substring(0, MACHINE_LABEL_LENGTH)
                    },
                    id: assetId,
                    position: { x: MACHINE_X, y: MACHINE_Y_VALUE },
                    type: "addAssetNode"
                });
                MACHINE_Y_VALUE = MACHINE_Y_VALUE + NODE_Y_STEP;
            }
        });
        return nodeList;
    };
    const nodeTypes = useMemo(() => ({
        addAssetNode: AddAssetNode,
        utilityPowerNode: UtilityPowerNode,
        utilityProviderNode: UtilityProviderNode
    }), []);
    useEffect(() => {
        const nodeListInfo = getNetworkNodes(assetList);
        setNodes(nodeListInfo);
    }, [assetList]);
    return (_jsx("div", { className: "h-[100%] w-[100%]", children: _jsx(ReactFlow, { className: "bg-gray-100", "data-test": "add-asset-network-diagram-react-flow", deleteKeyCode: null, edges: [
                {
                    id: "p",
                    source: "Power",
                    style: { border: "1px solid #000000", strokeWidth: EDGE_STROKE_WIDTH },
                    target: "utilityProviderNode"
                }
            ], nodeTypes: nodeTypes, nodes: nodes, nodesDraggable: false, onInit: (instance) => {
                instance.setViewport(DEFAULT_VIEWPORT);
            }, onNodesChange: onNodesChange, proOptions: { hideAttribution: true }, children: _jsx(Controls, { "data-test": "add-asset-network-diagram-controls", position: "bottom-right", showFitView: false, showInteractive: false }) }) }));
};
const AddAssetNetworkDiagram = (props) => {
    return (_jsx(ReactFlowProvider, { children: _jsx(AddAssetNetworkDiagramUI, { ...props }) }));
};
export default memo(AddAssetNetworkDiagram);
